import { useEffect, useRef, useState } from "react";
import { Select } from "antd";
import axios from "axios";
import { mainUrl } from "../../helpers/constants";

// Скоуп дашборда без ввода ObjectId: тип измерения + поиск значения по имени.
// paramKey — ключ, который уходит в тело обоих stat-эндпоинтов.
export type ScopeType = "portfolio" | "site" | "city" | "company";
export interface Scope {
  type: ScopeType;
  paramKey?: "url" | "cityId" | "companyId";
  value?: string;
  label?: string;
}

interface IProps {
  value: Scope;
  onChange: (s: Scope) => void;
}

const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

export const ScopePicker = ({ value, onChange }: IProps) => {
  const [options, setOptions] = useState<{ value: string; label: string }[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const companiesRef = useRef<{ value: string; label: string }[] | null>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // при смене типа — сбрасываем значение и грузим стартовые опции
  useEffect(() => {
    setOptions([]);
    if (value.type === "company") loadCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.type]);

  const loadCompanies = async () => {
    if (companiesRef.current) {
      setOptions(companiesRef.current);
      return;
    }
    try {
      setLoading(true);
      const r = await axios.get(`${mainUrl}api/v1/company`, auth());
      const opts = (r.data?.companies || r.data?.data || [])
        .map((c: any) => ({ value: c._id, label: c.name || c._id }))
        .filter((o: any) => o.label);
      companiesRef.current = opts;
      setOptions(opts);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const search = (q: string) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (value.type === "company") {
      const all = companiesRef.current || [];
      setOptions(
        q
          ? all.filter((o) => o.label.toLowerCase().includes(q.toLowerCase()))
          : all
      );
      return;
    }
    if (!q || q.length < 2) return;
    searchTimer.current = setTimeout(async () => {
      try {
        setLoading(true);
        if (value.type === "site") {
          const r = await axios.get(
            `${mainUrl}api/v1/blog/site-search?q=${encodeURIComponent(q)}`,
            auth()
          );
          setOptions(
            (r.data?.sites || [])
              .filter((s: any) => s.domain)
              .map((s: any) => ({
                value: s.domain,
                label: `${s.domain}${s.name ? ` · ${s.name}` : ""}`,
              }))
          );
        } else if (value.type === "city") {
          const r = await axios.get(
            `${mainUrl}api/v1/city/sug?q=${encodeURIComponent(q)}`,
            auth()
          );
          // ответ: { cities: [name, id][] }
          setOptions(
            (r.data?.cities || []).map((c: [string, string]) => ({
              value: c[1],
              label: c[0],
            }))
          );
        }
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 280);
  };

  const paramKeyFor = (t: ScopeType): Scope["paramKey"] =>
    t === "site" ? "url" : t === "city" ? "cityId" : t === "company" ? "companyId" : undefined;

  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
      <Select
        value={value.type}
        style={{ width: 150 }}
        onChange={(t: ScopeType) =>
          onChange({ type: t, paramKey: paramKeyFor(t) })
        }
        options={[
          { value: "portfolio", label: "Whole portfolio" },
          { value: "site", label: "Site" },
          { value: "city", label: "City" },
          { value: "company", label: "Company" },
        ]}
      />
      {value.type !== "portfolio" && (
        <Select
          showSearch
          allowClear
          loading={loading}
          style={{ width: 260 }}
          placeholder={
            value.type === "site"
              ? "Search domain / name…"
              : value.type === "city"
              ? "Type ≥2 letters…"
              : "Pick company…"
          }
          value={value.value}
          filterOption={false}
          onSearch={search}
          onFocus={() => value.type === "company" && loadCompanies()}
          onChange={(v, opt: any) =>
            onChange({
              type: value.type,
              paramKey: paramKeyFor(value.type),
              value: v,
              label: opt?.label,
            })
          }
          onClear={() =>
            onChange({ type: value.type, paramKey: paramKeyFor(value.type) })
          }
          options={options}
          notFoundContent={loading ? "…" : "No matches"}
        />
      )}
    </div>
  );
};
